// Marketing landing — Instacart-inspired layout, EN/FR/HT via react-i18next.
//
// Structure:
//   1. Sticky header (logo, nav, language switcher, sign in, get started)
//   2. Hero: big headline + recipient phone input + primary CTA + receipt card
//   3. Category grid (Groceries, Pharmacy, Tuition, Mobile Top-up)
//   4. How it works — 3 steps with numbered badges
//   5. Trust strip — KYB, recipient-PIN, Stripe
//   6. Get-the-app CTA (mobile PWA install pitch)
//   7. Footer
//
// Every visible string is t()-keyed. Translations live in src/locales/.

import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ShoppingBag, Pill, GraduationCap, Smartphone,
  ShieldCheck, MessageCircle, CheckCircle2, ArrowRight, Phone,
  Lock, MapPin, Heart, Smartphone as PhoneIcon, Store, Loader2, Package,
} from 'lucide-react';
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher';
import { CurrencySwitcher } from '../components/shared/CurrencySwitcher';
import { storefront, carePackages, type MerchantItem, type CarePackageSummary } from '../lib/api';
import { iconFor } from '../lib/icons';
import { formatHtgMinor, formatDisplayFromHtgMinor } from '../lib/money';

export function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SiteHeader />
      <Hero />
      <CategoryGrid />
      <CarePackagesStrip />
      <StorefrontStrip />
      <HowItWorks />
      <TrustStrip />
      <GetTheApp />
      <SiteFooter />
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────

function SiteHeader() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
            <ShoppingBag className="w-4 h-4" />
          </span>
          <span className="font-semibold tracking-tight text-[17px]">VoyeLakay</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-[13.5px] text-slate-600">
          <a href="#categories" className="hover:text-slate-900">{t('nav.categories')}</a>
          <Link to="/packages" className="hover:text-slate-900">Care packages</Link>
          <a href="#how" className="hover:text-slate-900">{t('nav.how')}</a>
          <a href="#trust" className="hover:text-slate-900">{t('nav.trust')}</a>
          <Link to="/claim" className="hover:text-slate-900">For merchants</Link>
        </nav>
        <div className="flex items-center gap-2">
          <CurrencySwitcher />
          <LanguageSwitcher />
          <Link to="/login" className="hidden sm:inline text-[13px] text-slate-700 hover:text-slate-900 px-3 py-2">{t('nav.signIn')}</Link>
          <Link
            to="/register?role=customer"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-slate-900 text-white text-[13px] font-semibold hover:bg-slate-800 transition-colors"
          >
            {t('nav.getStarted')}
          </Link>
        </div>
      </div>
    </header>
  );
}

// ─── Hero ────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-amber-50/40 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 border border-slate-200 text-[11px] uppercase tracking-[0.15em] text-slate-600 font-semibold">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t('hero.badge')}
          </span>
          <h1 className="mt-5 text-[42px] md:text-[64px] leading-[1.05] font-semibold tracking-tight text-slate-900">
            {t('hero.title1')}<br />
            <span className="text-brand-600">{t('hero.title2')}</span>
          </h1>
          <p className="mt-3 text-[15px] md:text-[17px] font-medium text-slate-500 tracking-tight">
            Send love home — verified, delivered, confirmed.
          </p>
          <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
            {t('hero.subtitle')} <strong className="text-slate-900">{t('hero.notTransfer')}</strong>
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const phone = new FormData(e.currentTarget).get('recipient_phone')?.toString().trim() || '';
              const qs = new URLSearchParams({ role: 'customer' });
              if (phone) qs.set('phone', phone);
              window.location.assign(`/register?${qs.toString()}`);
            }}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
          >
            <label className="relative flex-1 flex items-center bg-white rounded-xl border border-slate-200 shadow-sm focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-400">
              <Phone className="absolute left-4 w-4 h-4 text-slate-400" />
              <input
                type="tel"
                name="recipient_phone"
                placeholder={t('hero.phonePlaceholder')}
                className="w-full pl-11 pr-4 py-3.5 text-[15px] bg-transparent rounded-xl focus:outline-none placeholder:text-slate-400"
                autoComplete="off"
              />
            </label>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-1.5 px-6 py-3.5 rounded-xl bg-brand-600 text-white text-[15px] font-semibold hover:bg-brand-700 shadow-sm transition-colors"
            >
              {t('hero.start')} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="mt-4 text-[12px] text-slate-500">{t('hero.smallprint')}</p>
        </div>

        <div className="md:col-span-5">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  const { t } = useTranslation();
  const items: Array<[string, string]> = [
    [t('items.rice'),     'HTG 750'],
    [t('items.oil'),      'HTG 420'],
    [t('items.sugar'),    'HTG 180'],
    [t('items.eggs'),     'HTG 360'],
    [t('items.delivery'), 'HTG 250'],
  ];
  return (
    <div className="relative mx-auto max-w-md">
      <div className="absolute -inset-10 bg-gradient-to-br from-brand-200/40 to-amber-100/40 blur-3xl rounded-full" />
      <div className="relative bg-white rounded-3xl border border-slate-200 shadow-xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand-50 text-brand-600">
              <ShoppingBag className="w-4 h-4" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{t('card.request')}</p>
              <p className="text-[13px] font-semibold text-slate-900">REQ-1042 · {t('categories.groceries.title')}</p>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
            {t('card.confirmed')}
          </span>
        </div>

        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-1.5 text-[12px]">
          {items.map(([item, price]) => (
            <div key={item} className="flex items-center justify-between">
              <span className="text-slate-600">{item}</span>
              <span className="font-mono text-slate-900">{price}</span>
            </div>
          ))}
          <div className="border-t border-slate-200 my-2" />
          <div className="flex items-center justify-between font-semibold">
            <span className="text-slate-900">{t('card.youPay')}</span>
            <span className="text-slate-900 font-mono">$18.40 USD</span>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          {t('card.recipientConfirmed')}
        </div>
      </div>
    </div>
  );
}

// ─── Care packages strip (curated one-click bundles) ────────

function CarePackagesStrip() {
  const q = useQuery({
    queryKey: ['storefront', 'packages'],
    queryFn:  carePackages.list,
    staleTime: 5 * 60 * 1000,
  });

  // Hide the whole section if there's nothing to show (e.g. API down).
  if (q.data && q.data.length === 0) return null;

  return (
    <section className="border-t border-slate-100 py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold inline-flex items-center gap-1.5">
              <Package className="w-3 h-3" /> One-click care packages
            </p>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              Send a ready-made box home
            </h2>
            <p className="mt-2 text-[14px] text-slate-500 max-w-xl">
              Hand-picked bundles for the moments that matter — pick one, choose who it's for, and a merchant fills it.
            </p>
          </div>
          <Link to="/packages" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline shrink-0">
            See all packages <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {q.isLoading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-44 rounded-2xl border border-slate-100 bg-white animate-pulse" />
            ))}
          </div>
        )}

        {q.data && q.data.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {q.data.slice(0, 6).map((p) => <PackageStripCard key={p.id} pkg={p} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function PackageStripCard({ pkg }: { pkg: CarePackageSummary }) {
  const Icon = iconFor(pkg.system_icon);
  return (
    <Link
      to={`/packages/${pkg.slug}`}
      className="group bg-white border border-slate-100 rounded-2xl p-5 hover:border-slate-300 hover:shadow-md transition-all flex flex-col"
    >
      <div className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-50 to-amber-50/50 text-brand-600 mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-[15px] font-semibold text-slate-900">{pkg.name}</h3>
      {pkg.subtitle && <p className="mt-1 text-[12.5px] text-slate-500 leading-relaxed line-clamp-2">{pkg.subtitle}</p>}
      {pkg.merchant && (
        <p className="mt-1.5 text-[11px] text-slate-400 truncate">{pkg.merchant.business_name}{pkg.merchant.city ? ` · ${pkg.merchant.city.name}` : ''}</p>
      )}
      <div className="mt-auto pt-3 flex items-baseline justify-between">
        {pkg.approx_htg_minor != null ? (
          <span className="text-[13.5px] font-semibold text-slate-900 font-mono">≈ {formatDisplayFromHtgMinor(pkg.approx_htg_minor)}</span>
        ) : (
          <span className="text-[12px] text-slate-500">{pkg.items_count} items</span>
        )}
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 group-hover:gap-2 transition-all">
          View <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

// ─── Storefront strip (real merchant inventory) ─────────────

function StorefrontStrip() {
  // Public endpoint — no auth needed.
  const itemsQ = useQuery({
    queryKey: ['storefront', 'items', 12],
    queryFn:  () => storefront.items({ limit: 12 }),
    staleTime: 5 * 60 * 1000,                 // marketing page; 5min cache is fine
  });

  return (
    <section className="bg-gradient-to-b from-white to-slate-50 border-t border-slate-100 py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold inline-flex items-center gap-1.5">
              <Store className="w-3 h-3" /> In stock today
            </p>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              Shop real items from Léogâne merchants
            </h2>
            <p className="mt-2 text-[14px] text-slate-500 max-w-xl">
              Pulled live from approved merchants' inventory. Pick what your family needs — they'll see the quote in USD before you pay.
            </p>
          </div>
          <Link
            to="/register?role=customer"
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline shrink-0"
          >
            Sign in to shop the full catalog <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {itemsQ.isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="h-36 rounded-xl border border-slate-100 bg-white animate-pulse" />
            ))}
          </div>
        )}

        {itemsQ.error && (
          <div className="text-center py-10 text-[13px] text-slate-500 inline-flex items-center gap-2 justify-center">
            <Loader2 className="w-4 h-4" /> Couldn't load live inventory right now.
          </div>
        )}

        {itemsQ.data && itemsQ.data.length === 0 && (
          <div className="text-center py-10 text-[13px] text-slate-500">
            No items live yet — merchants are still loading inventory.
          </div>
        )}

        {itemsQ.data && itemsQ.data.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {itemsQ.data.map((it) => <StoreItemCard key={it.id} item={it} />)}
          </div>
        )}

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          Prices set by individual merchants. USD shown at ≈ 132.4 HTG / USD; the rate locks at quote time.
        </p>
      </div>
    </section>
  );
}

function StoreItemCard({ item }: { item: MerchantItem }) {
  const Icon = iconFor(item.system_icon);
  return (
    <Link
      to="/register?role=customer"
      className="group bg-white rounded-xl border border-slate-100 p-3 hover:border-slate-300 hover:shadow-md transition-all flex flex-col"
    >
      <div className="aspect-square rounded-lg bg-gradient-to-br from-brand-50 to-amber-50/40 flex items-center justify-center text-brand-600 mb-3">
        <Icon className="w-9 h-9" />
      </div>
      <p className="text-[12.5px] font-semibold text-slate-900 leading-tight line-clamp-2">{item.name}</p>
      <p className="text-[10.5px] text-slate-500 mt-0.5">{item.unit}</p>
      <div className="mt-auto pt-2 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-slate-900 font-mono">
          {formatHtgMinor(item.price_htg_minor)}
        </span>
        <span className="text-[10px] text-slate-400 font-mono">≈ {formatDisplayFromHtgMinor(item.price_htg_minor)}</span>
      </div>
      {item.merchant?.business_name && (
        <p className="text-[10px] text-slate-400 mt-1 truncate">{item.merchant.business_name}</p>
      )}
    </Link>
  );
}

// ─── Categories ──────────────────────────────────────────────

function CategoryGrid() {
  const { t } = useTranslation();
  const cats = [
    { key: 'groceries', icon: ShoppingBag,    color: 'bg-amber-50 text-amber-700' },
    { key: 'pharmacy',  icon: Pill,           color: 'bg-rose-50 text-rose-700' },
    { key: 'tuition',   icon: GraduationCap,  color: 'bg-blue-50 text-blue-700' },
    { key: 'topup',     icon: Smartphone,     color: 'bg-violet-50 text-violet-700' },
  ] as const;
  return (
    <section id="categories" className="border-t border-slate-100 py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold">{t('categories.kicker')}</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t('categories.title')}</h2>
          </div>
          <p className="text-[14px] text-slate-500 max-w-md">{t('categories.more')}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.map((c) => (
            <Link
              key={c.key}
              to="/register?role=customer"
              className="group bg-white border border-slate-100 rounded-2xl p-5 hover:border-slate-300 hover:shadow-md transition-all"
            >
              <div className={`inline-flex w-12 h-12 items-center justify-center rounded-xl ${c.color} mb-4`}>
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="text-[15px] font-semibold text-slate-900">{t(`categories.${c.key}.title`)}</h3>
              <p className="mt-1 text-[12.5px] text-slate-500 leading-relaxed">{t(`categories.${c.key}.blurb`)}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 group-hover:gap-2 transition-all">
                {t('categories.start')} <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── How it works ────────────────────────────────────────────

function HowItWorks() {
  const { t } = useTranslation();
  const steps = [
    { n: '01', icon: MessageCircle, key: 'step1' },
    { n: '02', icon: ShoppingBag,   key: 'step2' },
    { n: '03', icon: CheckCircle2,  key: 'step3' },
  ] as const;
  return (
    <section id="how" className="bg-slate-50 border-y border-slate-100 py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold">{t('how.kicker')}</p>
        <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 max-w-2xl">{t('how.title')}</h2>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl border border-slate-200 p-6 relative overflow-hidden">
              <span className="absolute -top-2 -right-2 text-[80px] font-bold text-slate-100 leading-none select-none">{s.n}</span>
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 relative">
                <s.icon className="w-5 h-5" />
              </span>
              <h3 className="mt-4 text-[16px] font-semibold text-slate-900 relative">{t(`how.${s.key}.title`)}</h3>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed relative">{t(`how.${s.key}.body`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Trust strip ─────────────────────────────────────────────

function TrustStrip() {
  const { t } = useTranslation();
  const points = [
    { icon: ShieldCheck, key: 'kyb' },
    { icon: Lock,        key: 'pin' },
    { icon: Heart,       key: 'stripe' },
  ] as const;
  return (
    <section id="trust" className="py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid md:grid-cols-3 gap-6">
          {points.map((p) => (
            <div key={p.key} className="flex items-start gap-4">
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-slate-900 text-white shrink-0">
                <p.icon className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">{t(`trust.${p.key}.title`)}</h3>
                <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">{t(`trust.${p.key}.body`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Get the app ─────────────────────────────────────────────

function GetTheApp() {
  const { t } = useTranslation();
  return (
    <section className="bg-gradient-to-br from-brand-600 to-brand-700 text-white">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-brand-100/90 font-semibold">{t('app.kicker')}</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">{t('app.title')}</h2>
          <p className="mt-4 text-brand-50/90 text-[15px] max-w-lg">
            <Trans
              i18nKey="app.body"
              values={{ url: 'lakaycart.com' }}
              components={{ 1: <code className="bg-white/10 rounded px-1.5 py-0.5 text-[13px]" /> }}
            />
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link to="/recipient" className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-white text-brand-700 text-[14px] font-semibold hover:bg-slate-50 transition-colors">
              <PhoneIcon className="w-4 h-4" /> {t('app.open')}
            </Link>
            <a href="#how" className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white text-[14px] font-semibold hover:bg-white/20 transition-colors">
              {t('app.seeHow')}
            </a>
          </div>
        </div>
        <div className="flex justify-center md:justify-end">
          <div className="relative w-[260px] h-[440px] rounded-[36px] bg-slate-900 border-8 border-slate-900 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-white p-4 flex flex-col">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">VoyeLakay</p>
              <h3 className="mt-1 text-[18px] font-semibold text-slate-900">{t('app.phone.header')}</h3>
              <p className="mt-1 text-[12px] text-slate-600">{t('app.phone.sub')}</p>
              <button className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg bg-brand-600 text-white text-[13px] font-semibold">
                {t('app.phone.new')}
              </button>
              <p className="mt-6 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t('app.phone.recent')}</p>
              <div className="mt-2 rounded-lg border border-slate-100 p-3 text-[11px]">
                <p className="font-semibold text-slate-900">{t('categories.groceries.title')} · REQ-1042</p>
                <p className="text-emerald-700 mt-0.5">{t('app.phone.delivered')}</p>
              </div>
              <div className="mt-2 rounded-lg border border-slate-100 p-3 text-[11px]">
                <p className="font-semibold text-slate-900">{t('categories.pharmacy.title')} · REQ-1039</p>
                <p className="text-slate-500 mt-0.5">{t('app.phone.awaiting')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────

function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="bg-white border-t border-slate-100">
      <div className="mx-auto max-w-7xl px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
              <ShoppingBag className="w-4 h-4" />
            </span>
            <span className="font-semibold tracking-tight text-[15px]">VoyeLakay</span>
          </div>
          <p className="mt-3 text-[13px] text-slate-500 leading-relaxed max-w-sm">{t('footer.tagline')}</p>
          <p className="mt-4 text-[12px] text-slate-400 inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {t('footer.city')}
          </p>
          <div className="mt-5"><LanguageSwitcher variant="labeled" /></div>
        </div>
        <FooterCol title={t('footer.product')} links={[[t('nav.categories'), '#categories'], [t('nav.how'), '#how'], [t('nav.trust'), '#trust']]} />
        <FooterCol title={t('footer.for')} links={[[t('footer.diaspora'), '/register?role=customer'], [t('footer.recipients'), '/recipient'], [t('footer.merchants'), '/register?role=merchant']]} />
        <FooterCol title={t('footer.company')} links={[[t('nav.signIn'), '/login'], [t('footer.contact'), 'mailto:hello@lakaycart.com'], [t('footer.status'), '/']]} />
      </div>
      <div className="border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-[12px] text-slate-500">
          <p>{t('footer.copyright')}</p>
          <p>
            <Link to="/privacy" className="hover:text-slate-900">{t('footer.privacy')}</Link>
            <span className="mx-2 text-slate-300">·</span>
            <Link to="/terms" className="hover:text-slate-900">{t('footer.terms')}</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{title}</p>
      <ul className="mt-3 space-y-2 text-[13px] text-slate-600">
        {links.map(([label, href]) => (
          <li key={label}>
            {href.startsWith('/') ? (
              <Link to={href} className="hover:text-slate-900">{label}</Link>
            ) : (
              <a href={href} className="hover:text-slate-900">{label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
